import { useEffect, useRef, useState } from 'react';
import type { MarketSelection, MarketView, OddsFormat } from '@hienbet/shared';
import { api, ApiError, formatChips, formatClock, formatOddsValue, loginUrl, type Me } from '../api.js';
import { useCountdown, type LiveState } from '../hooks/useLive.js';
import { PlayerName } from '../components/PlayerName.js';
import { Logo } from '../components/Logo.js';

interface Pick {
  market: MarketView;
  selection: MarketSelection;
}

const QUICK_STAKES = [100, 500, 1_000, 5_000];

export function BookPage({
  live,
  me,
  onBalance,
  oddsFormat,
}: {
  live: LiveState;
  me: Me | null;
  onBalance: () => void;
  oddsFormat: OddsFormat;
}) {
  // Picks accumulate across markets: one is a straight bet, two to four make
  // a parlay. Picking again in the same market replaces that leg; picking the
  // same selection removes it.
  const [picks, setPicks] = useState<Pick[]>([]);
  const { book } = live;

  const togglePick = (pick: Pick) => {
    setPicks((prev) => {
      const existing = prev.find((p) => p.market.id === pick.market.id);
      if (existing?.selection.id === pick.selection.id) {
        return prev.filter((p) => p.market.id !== pick.market.id);
      }
      const rest = prev.filter((p) => p.market.id !== pick.market.id);
      return rest.length >= 4 ? prev : [...rest, pick];
    });
  };

  const open = book.markets.filter((m) => m.state === 'open');
  const locked = book.markets.filter((m) => m.state === 'locked');
  const done = book.markets.filter((m) => m.state === 'settled' || m.state === 'voided');

  if (!book.live) {
    return (
      <div className="page">
        <Idle connected={live.connected} />
      </div>
    );
  }

  return (
    <div className="page book-layout">
      <div>
        <GameStrip live={live} />

        {open.length > 0 && (
          <MarketList markets={open} picks={picks} onPick={togglePick} oddsFormat={oddsFormat} />
        )}

        {book.phase === 'pregame' && open.length > 0 && (
          <p className="note">
            Lines move a lot during champion select — every pick, ban, and enemy-lobby lookup
            reprices the board in real time. The most dialed-in prices come once her champion
            locks; betting early means taking a price the analysis hasn&rsquo;t finished with.
            Whatever price you take is locked into your bet and never changes.
          </p>
        )}

        {open.length === 0 && locked.length > 0 && (
          <p className="note">Betting is closed for this game. Results settle when it ends.</p>
        )}

        {locked.length > 0 && (
          <>
            <h2 className="section">Locked</h2>
            <MarketList markets={locked} picks={[]} onPick={() => undefined} oddsFormat={oddsFormat} />
          </>
        )}

        {done.length > 0 && (
          <>
            <h2 className="section">Settled</h2>
            <MarketList markets={done} picks={[]} onPick={() => undefined} oddsFormat={oddsFormat} />
          </>
        )}

        <ActionFeed live={live} />
      </div>

      <BetSlip
        picks={picks}
        me={me}
        onRemove={(marketId) => setPicks((prev) => prev.filter((p) => p.market.id !== marketId))}
        onClear={() => setPicks([])}
        onPlaced={onBalance}
        oddsFormat={oddsFormat}
      />
    </div>
  );
}

/** Recent big bets and wins. Only stakes over the threshold reach the feed. */
function ActionFeed({ live }: { live: LiveState }) {
  if (live.ticker.length === 0) return null;

  return (
    <>
      <h2 className="section">Action</h2>
      <ul className="action-feed">
        {live.ticker.slice(0, 12).map((entry) => (
          <li key={entry.id}>
            <span className="action-who">
              <PlayerName name={entry.displayName} style={entry.style} />
            </span>
            <span className="action-verb">{entry.kind === 'payout' ? 'won' : 'bet'}</span>
            <span className={`action-amount num ${entry.kind}`}>
              {formatChips(entry.amount)}
            </span>
            <span className="action-market">{entry.marketLabel}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function Idle({ connected }: { connected: boolean }) {
  return (
    <div className="idle">
      <div className="idle-mark">
        <Logo size={120} />
      </div>
      <div className="idle-copy">
        <h1>NOTHING ON THE BOARD</h1>
        <p>
          {connected
            ? 'The second Hien queues ranked on stream, lines open here — win, kills, deaths, first blood. Odds come off her real match history, not vibes.'
            : 'Reconnecting to the book…'}
        </p>
        <p className="idle-fine">
          Ranked only. Betting locks 3:00 into every game. Chips are worthless — that&rsquo;s the
          point.
        </p>
      </div>
    </div>
  );
}

function GameStrip({ live }: { live: LiveState }) {
  const { book } = live;
  const countdown = useCountdown(book.lockInSeconds);
  const closing = countdown !== null && countdown <= 30;

  // The strip has to speak for every phase, not just "counting down" or "shut".
  // Saying LIVE over a finished game, or CLOSED during champion select, are both
  // lies that cost trust — and in the second case, bets.
  const beforeGame = book.phase === 'pregame';
  const finished = book.phase === 'settled';
  const voided = book.phase === 'voided';
  const frozen = book.phase === 'frozen';
  const bettingOpen = book.markets.some((m) => m.state === 'open');

  const over = finished || voided;

  return (
    <div className={`gamestrip ${over ? 'is-over' : ''}`}>
      <span className={`live-tag ${over ? 'ended' : ''}`}>
        {!over && <i />}
        {voided ? 'VOIDED' : finished ? 'FINAL' : beforeGame ? 'IN QUEUE' : 'LIVE'}
      </span>

      {beforeGame ? (
        <span className="gamestrip-status">Champion select — game hasn&rsquo;t started</span>
      ) : (
        <span className="gamestrip-clock num">{formatClock(book.gameTime)}</span>
      )}

      {frozen && (
        <span className="gamestrip-status warn">
          Feed interrupted — nothing settles until we can prove the result
        </span>
      )}

      {voided ? (
        <span className="lockbox">
          <span className="label">All bets</span>
          <span className="lock-num closed">REFUNDED</span>
        </span>
      ) : finished ? (
        <span className="lockbox">
          <span className="label">Game</span>
          <span className="lock-num closed">COMPLETE</span>
        </span>
      ) : countdown !== null ? (
        <span className={`lockbox ${closing ? 'closing' : ''}`}>
          <span className="label">Betting closes</span>
          <span className="num lock-num">{formatClock(countdown)}</span>
        </span>
      ) : bettingOpen ? (
        <span className="lockbox">
          <span className="label">Betting</span>
          <span className="lock-num is-open">OPEN</span>
        </span>
      ) : (
        <span className="lockbox">
          <span className="label">Betting</span>
          <span className="lock-num closed">CLOSED</span>
        </span>
      )}
    </div>
  );
}

/**
 * Which prices just moved, and in which direction.
 *
 * A real board tells you the line is alive: when a price lengthens it flashes
 * green (better payout), when it shortens it flashes red (the window closing).
 * Purely visual — the server has already repriced; this just makes it felt.
 */
function useOddsFlashes(markets: MarketView[]): Map<string, 'up' | 'down'> {
  const previous = useRef(new Map<string, number>());
  const flashes = new Map<string, 'up' | 'down'>();

  for (const market of markets) {
    for (const selection of market.selections) {
      const key = `${market.id}:${selection.id}`;
      const before = previous.current.get(key);
      if (before !== undefined && before !== selection.milliOdds) {
        flashes.set(key, selection.milliOdds > before ? 'up' : 'down');
      }
    }
  }

  useEffect(() => {
    for (const market of markets) {
      for (const selection of market.selections) {
        previous.current.set(`${market.id}:${selection.id}`, selection.milliOdds);
      }
    }
  });

  return flashes;
}

function MarketList({
  markets,
  picks,
  onPick,
  oddsFormat,
}: {
  markets: MarketView[];
  picks: Pick[];
  onPick: (pick: Pick) => void;
  oddsFormat: OddsFormat;
}) {
  const flashes = useOddsFlashes(markets);

  return (
    <div className="markets">
      {markets.map((market) => (
        <div key={market.id} className={`market ${market.state}`}>
          <div className="market-name">
            {market.label}
            {market.line !== null && <em className="market-line"> {market.line}</em>}
            {market.state === 'voided' && <span className="void-tag">void — stake returned</span>}
          </div>

          <div className="market-odds">
            {market.selections.map((selection) => {
              const isWinner = market.result === selection.id;
              const isPicked = picks.some(
                (p) => p.market.id === market.id && p.selection.id === selection.id,
              );
              const interactive = market.state === 'open';
              const flash = flashes.get(`${market.id}:${selection.id}`);

              return (
                <button
                  key={selection.id}
                  type="button"
                  disabled={!interactive}
                  className={`odds ${isPicked ? 'picked' : ''} ${isWinner ? 'won' : ''}`}
                  onClick={() => interactive && onPick({ market, selection })}
                >
                  <span className="odds-label">{selection.label}</span>
                  <span
                    // Key includes the price so a move remounts the span and
                    // the flash animation runs exactly once per change.
                    key={`${selection.milliOdds}`}
                    className={`odds-value num ${flash ? `flash-${flash}` : ''}`}
                  >
                    {formatOddsValue(selection.milliOdds, oddsFormat)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BetSlip({
  picks,
  me,
  onRemove,
  onClear,
  onPlaced,
  oddsFormat,
}: {
  picks: Pick[];
  me: Me | null;
  onRemove: (marketId: string) => void;
  onClear: () => void;
  onPlaced: () => void;
  oddsFormat: OddsFormat;
}) {
  const [stake, setStake] = useState('500');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState<{ stake: number; odds: number; legs: number } | null>(null);

  const amount = Number(stake);
  const valid = Number.isInteger(amount) && amount > 0;
  const balance = me?.balance ?? 0;
  const affordable = valid && amount <= balance;

  const isParlay = picks.length > 1;
  // Parlay odds multiply, capped like the server caps them.
  const combinedMilli = Math.min(
    100_000,
    picks.reduce((m, p) => Math.floor((m * p.selection.milliOdds) / 1000), 1000),
  );
  // With American odds the convention is to show profit, not total return —
  // "+104" means you win 104, so that is the number that must match.
  const returns = valid && picks.length > 0 ? Math.floor((amount * combinedMilli) / 1000) : 0;
  const toWin = returns - (valid ? amount : 0);

  if (picks.length === 0) {
    return (
      <aside className="slip empty">
        <span className="label">Bet slip</span>
        <p>Pick a price to bet. Pick prices in several markets to build a parlay.</p>
        {placed && (
          <p className="slip-done">
            {placed.legs > 1 ? `Parlay ×${placed.legs}` : 'Bet'} placed: {formatChips(placed.stake)} at{' '}
            {formatOddsValue(placed.odds, oddsFormat)}
          </p>
        )}
      </aside>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isParlay) {
        await api.placeParlay({
          stake: amount,
          legs: picks.map((p) => ({
            marketId: p.market.id,
            selectionId: p.selection.id,
            // If any price moved between render and click, the server rejects
            // rather than filling at numbers the bettor never agreed to.
            expectedMilliOdds: p.selection.milliOdds,
          })),
        });
        setPlaced({ stake: amount, odds: combinedMilli, legs: picks.length });
      } else {
        const pick = picks[0]!;
        await api.placeBet({
          marketId: pick.market.id,
          selectionId: pick.selection.id,
          stake: amount,
          expectedMilliOdds: pick.selection.milliOdds,
        });
        setPlaced({ stake: amount, odds: pick.selection.milliOdds, legs: 1 });
      }
      onPlaced();
      onClear();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not place that bet.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="slip">
      <div className="slip-head">
        <span className="label">{isParlay ? `Parlay ×${picks.length}` : 'Bet slip'}</span>
        <button className="ghost tiny" onClick={onClear}>
          clear
        </button>
      </div>

      {picks.map((pick) => (
        <div className="slip-pick" key={pick.market.id}>
          <div className="slip-selection">{pick.selection.label}</div>
          <div className="slip-market">
            {pick.market.label}
            {pick.market.line !== null && ` ${pick.market.line}`}
          </div>
          <div className="slip-odds num">{formatOddsValue(pick.selection.milliOdds, oddsFormat)}</div>
          {isParlay && (
            <button
              className="slip-remove"
              title="Remove this leg"
              onClick={() => onRemove(pick.market.id)}
            >
              ×
            </button>
          )}
        </div>
      ))}

      {isParlay && (
        <div className="slip-combined">
          <span className="label">Combined</span>
          <span className="num slip-combined-odds">{formatOddsValue(combinedMilli, oddsFormat)}</span>
        </div>
      )}

      {me?.loggedIn ? (
        <>
          <div className="stake-row">
            <input
              className="stake num"
              inputMode="numeric"
              value={stake}
              aria-label="Stake"
              onChange={(e) => setStake(e.target.value.replace(/[^\d]/g, ''))}
            />
            <div className="quick">
              {QUICK_STAKES.map((value) => (
                <button key={value} className="ghost tiny" onClick={() => setStake(String(value))}>
                  {value >= 1000 ? `${value / 1000}k` : value}
                </button>
              ))}
              <button className="ghost tiny" onClick={() => setStake(String(balance))}>
                max
              </button>
            </div>
          </div>

          <div className="towin">
            <div>
              <span className="label">To win</span>
              <span className="towin-sub">returns {formatChips(returns)}</span>
            </div>
            <span className="num towin-value">+{formatChips(toWin)}</span>
          </div>

          {!affordable && valid && <p className="slip-error">You only have {formatChips(balance)} chips.</p>}
          {error && <p className="slip-error">{error}</p>}

          <button className="place" disabled={!affordable || busy} onClick={submit}>
            {busy ? 'Placing…' : isParlay ? `Place parlay ×${picks.length}` : 'Place bet'}
          </button>
          <p className="slip-fine">
            {isParlay
              ? 'All legs must hit. A voided leg drops out and the rest still ride. Prices lock when placed.'
              : 'Your price is locked in when the bet is placed, even if the line moves after.'}
          </p>
        </>
      ) : (
        <a className="place as-link" href={loginUrl()}>
          Log in with Twitch to bet
        </a>
      )}
    </aside>
  );
}
